import React, { useState, useEffect } from "react";
import {
    TextField,
    Avatar,
    Skeleton,
    Typography,
    Box,
    InputAdornment,
    Dialog,
    DialogContent,
    CircularProgress,
    Alert,
    Button, // Import Button
    IconButton, // Import IconButton for potential close button later if needed
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
// import CloseIcon from '@mui/icons-material/Close'; // Keep if you might add a close button
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../../utils/constants"; // Assuming this holds the base URL

// --- Constants for Image URLs (Consider moving to constants.js) ---
const ODIN_USER_IMAGE_BASE_URL = "https://images.odin.fun/user";
const ODIN_TOKEN_IMAGE_BASE_URL = "https://images.odin.fun/token";
const USER_API_URL = "https://api.odin.fun/v1/user"; // Keep separate or derive if possible

// --- SearchDialog Component ---
function SearchDialog({ open, handleClose }) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");
    const [users, setUsers] = useState([]);
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    // --- Effect for fetching search-as-you-type results ---
    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        if (inputValue.trim().length < 2) {
            setUsers([]);
            setTokens([]);
            setLoading(false);
            controller.abort();
            return;
        }

        const fetchResults = async () => {
            setLoading(true);
            if (error) setError(null);
            try {
                const { data } = await axios.get(
                    `${ API_BASE_URL }/search?q=${ inputValue }`,
                    { signal: controller.signal }
                );

                if (active) {
                    const fetchedUsers = data.users || [];
                    const userOptions = fetchedUsers.map((userId) => ({
                        id: userId,
                        type: "user",
                    }));
                    setUsers(userOptions);

                    const fetchedTokens = data.tokens || [];
                    const tokenOptions = fetchedTokens.map((t) => ({
                        id: t.id,
                        name: t.name,
                        ticker: t.ticker,
                        creator: t.creator,
                        type: "token",
                    }));
                    setTokens(tokenOptions);
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log("Search request cancelled");
                } else {
                    console.error("Error fetching search results:", error);
                    if (active) {
                        setUsers([]);
                        setTokens([]);
                        setError(error);
                    }
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        // Debounce the fetch request slightly to avoid firing on every keystroke
        const timerId = setTimeout(() => {
            fetchResults();
        }, 300); // Adjust debounce time as needed (e.g., 300ms)


        return () => {
            active = false;
            clearTimeout(timerId); // Clear the timeout on cleanup
            controller.abort();
        };
    }, [inputValue]); // Only re-run when inputValue changes

    // --- Function to handle item selection from the list ---
    const handleSelect = (type, id) => {
        if (type === 'token') {
            window.open(`/tokens/${ id }`);
        } else if (type === 'user') {
            window.open(`/users/${ id }`);
        }
        handleClose();
    };

    // --- Function to handle the explicit Search button click or Enter key ---
    const handleDirectSearch = () => {
        const trimmedInput = inputValue.trim();
        if (!trimmedInput) {
            return; // Do nothing if input is empty
        }

        if (trimmedInput.includes('-')) {
            // Assume it's a user ID if it contains a dash
            window.open(`/users/${ trimmedInput }`);
        } else {
            // Assume it's a token ID or ticker otherwise
            window.open(`/tokens/${ trimmedInput }`);
        }
        handleClose(); // Close the dialog after navigating
    };

    // --- Handle Enter key press in the text field ---
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault(); // Prevent default form submission if applicable
            handleDirectSearch();
        }
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'flex-start',
                    paddingTop: '5vh',
                },
            }}
        >
            {/* Sticky Header for Search Input and Button */}
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "rgba(255,255,255,.1)",
                    zIndex: 1,
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex', // Arrange input and button horizontally
                    alignItems: 'center', // Align items vertically
                    gap: 1, // Add space between input and button
                }}
            >
                <TextField
                    fullWidth
                    autoFocus
                    variant="outlined"
                    placeholder={t('common.searchPlaceholder')} // Updated placeholder
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown} // Add KeyDown handler
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="grey.900" />
                            </InputAdornment>
                        ),
                        // Keep loading indicator if needed for the debounced search
                        endAdornment: loading ? <CircularProgress size={20} color={"secondary"} /> : null,
                    }}
                    sx={{
                        flexGrow: 1, // Allow TextField to take available space
                        '& .MuiOutlinedInput-root': {
                            // Optional: Adjust padding if needed due to button
                        }
                    }}
                />
                {/* Search Button */}
                <Button
                    variant="contained"
                    color="secondary" // Or "secondary" or another theme color
                    onClick={handleDirectSearch}
                    disabled={!inputValue.trim()} // Disable if input is empty/whitespace
                    sx={{ px: 3 }} // Add some horizontal padding to the button
                >
                    {t('common.searchButton')}
                </Button>
            </Box>

            {/* Scrollable Content Area */}
            <DialogContent dividers sx={{ flexGrow: 1, p: 0 }}>
                {/* Error Display */}
                {error && !loading && !users.length && !tokens.length && ( // Show error only if no results and not loading
                    <Box sx={{ p: 2 }}>
                        <Alert severity="warning">
                            {t('common.couldNotFindResults', {
                                inputValue,
                                error: error.message ? `(${ error.message })` : ''
                            })}
                        </Alert>
                    </Box>
                )}

                {/* Tokens Section */}
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('common.tokens')}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            minHeight: loading || tokens.length > 0 ? 'auto' : '60px',
                            alignItems: tokens.length === 0 && !loading ? 'center' : 'stretch',
                            justifyContent: tokens.length === 0 && !loading ? 'center' : 'flex-start',
                        }}
                    >
                        {loading && inputValue.trim().length >= 2 ? ( // Show skeleton only when loading *and* input is present
                            [...Array(3)].map((_, index) => (
                                <Skeleton key={`token-skel-${ index }`} variant="rectangular" height={60} />
                            ))
                        ) : tokens.length > 0 ? (
                            tokens.map((token) => (
                                <Box
                                    key={token.id}
                                    onClick={() => handleSelect('token', token.id)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1,
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Avatar
                                        src={`${ ODIN_TOKEN_IMAGE_BASE_URL }/${ token.id }`}
                                        alt={token.name || 'Token'}
                                        sx={{ width: 40, height: 40 }}
                                    />
                                    <Box sx={{ ml: 1.5 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                            {token.name || t('common.unnamedToken')}
                                        </Typography>
                                        {token.ticker && (
                                            <Typography variant="body2" color="text.secondary">
                                                ${token.ticker}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))
                        ) : (
                            !loading && inputValue.trim().length >= 2 && (
                                <Typography variant="body2" color="textSecondary" align="center">
                                    {t('common.noMatchingTokens')}
                                </Typography>
                            )
                        )}
                    </Box>
                </Box>

                {/* Users Section */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                        {t('common.users')}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            minHeight: loading || users.length > 0 ? 'auto' : '60px',
                            alignItems: users.length === 0 && !loading ? 'center' : 'stretch',
                            justifyContent: users.length === 0 && !loading ? 'center' : 'flex-start',
                        }}
                    >
                        {loading && inputValue.trim().length >= 2 ? ( // Show skeleton only when loading *and* input is present
                            [...Array(3)].map((_, index) => (
                                <Skeleton key={`user-skel-${ index }`} variant="rectangular" height={60} />
                            ))
                        ) : users.length > 0 ? (
                            users.map((user) => (
                                <UserOption key={user.id} userId={user.id} onSelect={handleSelect} />
                            ))
                        ) : (
                            !loading && inputValue.trim().length >= 2 && (
                                <Typography variant="body2" color="textSecondary" align="center">
                                    {t('common.noMatchingUsers')}
                                </Typography>
                            )
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}


// --- UserOption Component (keep as is or refactor if API changes) ---
const UserOption = ({ userId, onSelect }) => {
    const { t } = useTranslation();
    const [userData, setUserData] = useState(null);
    const [loadingUser, setLoadingUser] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        setLoadingUser(true);
        setError(null);

        const fetchUserData = async () => {
            try {
                const { data } = await axios.get(`${ USER_API_URL }/${ userId }`);
                if (active) {
                    setUserData(data);
                }
            } catch (err) {
                console.error(`Error fetching user data for ${ userId }:`, err);
                if (active) {
                    setError(t('common.failedToLoadUserData'));
                }
            } finally {
                if (active) {
                    setLoadingUser(false);
                }
            }
        };

        fetchUserData();

        return () => {
            active = false;
        };
    }, [userId]);

    const truncateId = (id) => {
        if (!id || id.length <= 16) return id;
        return `${ id.slice(0, 8) }...${ id.slice(-8) }`;
    };

    if (loadingUser || error || !userData) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    borderRadius: 1,
                }}
            >
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ ml: 1.5, flexGrow: 1 }}>
                    {error ? (
                        <Typography variant="body1" color="error">{error}</Typography>
                    ) : (
                        <>
                            <Skeleton variant="text" width="60%" />
                            <Skeleton variant="text" width="40%" />
                        </>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            onClick={() => onSelect('user', userId)}
            sx={{
                display: "flex",
                alignItems: "center",
                p: 1,
                borderRadius: 1,
                cursor: "pointer",
                '&:hover': {
                    backgroundColor: 'action.hover',
                },
            }}
        >
            <Avatar
                sx={{ width: 40, height: 40 }}
                src={`${ ODIN_USER_IMAGE_BASE_URL }/${ userId }`}
                alt={userData.username || 'User'}
            />
            <Box sx={{ ml: 1.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {userData.username || t('common.unnamedUser')}
                </Typography>
                <Typography variant="body2" component="div" color="text.secondary">
                    {truncateId(userId)}
                </Typography>
            </Box>
        </Box>
    );
};


// --- AsynchronousSearch (Main Trigger) Component (keep as is) ---
function AsynchronousSearch() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <TextField
                label={t('common.searchTokensAndUsers')}
                variant="outlined"
                fullWidth
                onClick={handleOpen}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <SearchIcon color="secondary" />
                        </InputAdornment>
                    ),
                }}
                InputLabelProps={{
                    shrink: false,
                }}
                sx={{
                    cursor: "pointer",
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#F48600' },
                        '&:hover fieldset': { borderColor: '#F48600' },
                        '&.Mui-focused fieldset': { borderColor: '#F48600' },
                    },
                    '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 16px) scale(1)',
                        transition: 'none',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        transform: 'translate(14px, 16px) scale(1)',
                    },
                }}
            />
            <SearchDialog open={open} handleClose={handleClose} />
        </>
    );
}

export default AsynchronousSearch;